import {
  infiniteQueryOptions,
  queryOptions,
} from '@tanstack/react-query';
import { getUsersMe } from './get-users-me';
import { getUsersMeCertificates } from './get-users-me-certificates';
import { getContentCompletionRequest } from './get-course-content-completion-requests';
import type { Pagination } from '@/shared/types/api';
import type { GetContentCompletionResponse } from '../model/dto';

export const usersQueries = {
  all: ['users'],
  me: () =>
    queryOptions({
      queryKey: [...usersQueries.all, 'me'],
      queryFn: () => getUsersMe(),
    }),
  certificates: () =>
    queryOptions({
      queryKey: [...usersQueries.all, 'certificates'],
      queryFn: () => getUsersMeCertificates(),
    }),
  completionRequests: (params: Pagination) =>
    infiniteQueryOptions<
      GetContentCompletionResponse,
      Error,
      GetContentCompletionResponse,
      (string | Pagination)[],
      Pagination | string
    >({
      queryKey: [...usersQueries.all, 'completion-requests', params],
      initialPageParam: params,
      queryFn: async ({ pageParam }) => {
        if (typeof pageParam === 'string') {
          const url = new URL(
            pageParam,
            typeof window === 'undefined' ? 'http://localhost' : window.location.origin
          );
          const cursor = url.searchParams.get('cursor') ?? undefined;
          const pageSizeValue = url.searchParams.get('page_size');
          const pageValue = url.searchParams.get('page');
          const pager = url.searchParams.get('pager') as Pagination['pager'] | null;

          const parseNumber = (value: string | null) => {
            if (!value) {
              return undefined;
            }
            const parsed = Number(value);
            return Number.isNaN(parsed) ? undefined : parsed;
          };

          const nextParams: Pagination = {
            ...params,
            cursor,
            pager: pager ?? params.pager,
            page_size: parseNumber(pageSizeValue) ?? params.page_size,
            page: parseNumber(pageValue) ?? params.page,
          };

          return getContentCompletionRequest(nextParams);
        }

        return getContentCompletionRequest(pageParam as Pagination);
      },
      getNextPageParam: (lastPage) => lastPage.next ?? undefined,
      refetchOnWindowFocus: false,
    }),
};
